import * as React from "react";
import type { Ordering } from "@/types/base";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import { deserializeColumnFilters, type ColumnFilters, type DateFilter } from "@/modules/snapshots/grid/sortAndFilter";
import { snapshotGridColumnName } from "@/modules/snapshots/grid/gridColumnKeys";
import type { SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";

export interface InitialViewGridState {
  hiddenColumns: string[];
  columnOrder: string[];
  noteColumnOrder: string[];
  columnFilters: ColumnFilters;
  ordering: Ordering;
  /** Only present for a fixed date range; other modes leave the current date filter untouched. */
  dateFilter?: DateFilter;
}

/** Translates a saved view's key-based configuration into the grid's name-based local state. */
export const mapViewConfigurationToGridState = (
  configuration: SnapshotViewConfiguration,
  fields: SnapshotGridField[],
): InitialViewGridState => {
  const names = (keys: string[]) =>
    keys.map(key => snapshotGridColumnName(key, fields)).filter((name): name is string => name !== null);
  const orderedFields = configuration.column_order
    .map(key => fields.find(field => field.key === key))
    .filter((field): field is SnapshotGridField => Boolean(field));
  const orderingName = snapshotGridColumnName(configuration.ordering.order_by, fields);

  const state: InitialViewGridState = {
    hiddenColumns: names(configuration.hidden_column_keys),
    columnOrder: orderedFields.filter(field => !field.is_note).map(field => field.name),
    noteColumnOrder: orderedFields.filter(field => field.is_note).map(field => field.name),
    columnFilters: deserializeColumnFilters(
      Object.fromEntries(
        Object.entries(configuration.column_filters).flatMap(([key, value]) => {
          const name = snapshotGridColumnName(key, fields);
          return name ? [[name, value]] : [];
        }),
      ),
    ),
    ordering: { ...configuration.ordering, order_by: orderingName ?? configuration.ordering.order_by },
  };

  if (configuration.date_range?.mode === "fixed") {
    state.dateFilter = {
      selectedField: snapshotGridColumnName(configuration.date_range.field_key, fields),
      startDate: configuration.date_range.start_date
        ? new Date(`${configuration.date_range.start_date}T00:00:00`)
        : null,
      endDate: configuration.date_range.end_date ? new Date(`${configuration.date_range.end_date}T00:00:00`) : null,
    };
  }

  return state;
};

interface Input {
  initialConfiguration?: SnapshotViewConfiguration;
  fields: SnapshotGridField[] | undefined;
  appliedRef: React.MutableRefObject<boolean>;
  setHiddenColumns: (columns: string[]) => void;
  setColumnOrder: (columns: string[]) => void;
  setNoteColumnOrder: (columns: string[]) => void;
  setColumnFilters: (filters: ColumnFilters) => void;
  setOrdering: (ordering: Ordering) => void;
  setDateFilter: (dateFilter: DateFilter) => void;
}

/** Applies a saved view's configuration once, as soon as the first page provides the field metadata. */
export const useInitialViewConfiguration = ({
  initialConfiguration,
  fields,
  appliedRef,
  setHiddenColumns,
  setColumnOrder,
  setNoteColumnOrder,
  setColumnFilters,
  setOrdering,
  setDateFilter,
}: Input) => {
  React.useEffect(() => {
    if (!initialConfiguration || appliedRef.current || !fields?.length) return;
    const state = mapViewConfigurationToGridState(initialConfiguration, fields);
    setHiddenColumns(state.hiddenColumns);
    setColumnOrder(state.columnOrder);
    setNoteColumnOrder(state.noteColumnOrder);
    setColumnFilters(state.columnFilters);
    setOrdering(state.ordering);
    if (state.dateFilter) setDateFilter(state.dateFilter);
    appliedRef.current = true;
  }, [
    appliedRef,
    fields,
    initialConfiguration,
    setColumnFilters,
    setColumnOrder,
    setDateFilter,
    setHiddenColumns,
    setNoteColumnOrder,
    setOrdering,
  ]);
};
